import type { RawAxiosRequestConfig } from 'axios';

import type { CreateUsersPasswordResetConfirmRequest } from '@spazious/client-backoffice';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';

import { useSwallowedMutation } from '../_useSwallowedMutation.js';
import { invalidateMutationKey } from '../_utils.js';
import { useApiClient } from '../useApiClient.js';

export type PasswordResetConfirmVariables = {
  password: string;
  token: string;
  requestOptions?: RawAxiosRequestConfig;
};

type UseUsersPasswordResetConfirmParams = {
  mutationKey?: QueryKey | QueryKey[];
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

export const useUsersPasswordResetConfirm = ({
  mutationKey,
  onSuccess,
  onError,
}: UseUsersPasswordResetConfirmParams = {}) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { mutateAsync: confirmPasswordReset, isPending } = useSwallowedMutation({
    mutationFn: async ({ password, token, requestOptions }: PasswordResetConfirmVariables) => {
      const options: RawAxiosRequestConfig = {
        ...(requestOptions ?? {}),
        headers: {
          'Content-Type': 'application/json',
          ...((requestOptions?.headers as Record<string, string> | undefined) ?? {}),
        },
      };
      // Boundary cast: the OpenAPI spec wrongly declares a JSON:API envelope for this
      // endpoint. The backend view (django-rest-passwordreset) parses flat JSON, which
      // is exactly what this sends (with the application/json override above) and what
      // production has always sent.
      const response = await apiClient.users.createUsersPasswordResetConfirm(
        {
          createUsersPasswordResetConfirmRequest: {
            password,
            token,
          } as unknown as CreateUsersPasswordResetConfirmRequest,
        },
        options
      );
      return response.data;
    },
    onSuccess: () => {
      onSuccess?.();
      invalidateMutationKey(queryClient, mutationKey);
    },
    onError,
  });

  return { confirmPasswordReset, isPending };
};
